import type { ComboboxData } from "@mantine/core";
import { Alert, Group, MultiSelect, Switch } from "@mantine/core";
import { useEffect, useState } from "react";
import type { FieldError } from "react-hook-form";
import { AlertPresets } from "../../../mantine/mantinePresets";
import { FormSection } from "../../BuildingBlocks/Forms/FormSection/FormSection";
import { CustomFormFieldWrapper } from "../../BuildingBlocks/Forms/MantineFormFields/CustomFormFieldWrapper/CustomFormFieldWrapper";
import {
  GENERATOR_FORM_DATA,
  paragraph_24_zusammenfassung_helpText
} from "../../ObjectModals/GeneratorModal/GeneratorModal.constants";
import type {
  Paragraph24GeneratorListEntry,
  TParagraph24Zusammenfassung
} from "./Paragraph24Zusammenfassung.types";
import { Paragraph24ZusammenfassungTable } from "./Paragraph24ZusammenfassungTable";

export interface Paragraph24ZusammenfassungProps {
  value: TParagraph24Zusammenfassung["custom_generator_ids"];
  onChange: (
    value: TParagraph24Zusammenfassung["custom_generator_ids"]
  ) => void;
  lockedValues: Array<number>;
  error?: FieldError;
  defaultGenerators?: ReadonlyArray<Paragraph24GeneratorListEntry>;
  warning?: TParagraph24Zusammenfassung["warning"];
}

function Paragraph24Zusammenfassung({
  value,
  onChange,
  lockedValues = [],
  error,
  defaultGenerators,
  warning
}: Paragraph24ZusammenfassungProps) {
  const [manualEdit, setManualEdit] = useState(!!value);
  useEffect(() => {
    if (value && !manualEdit) {
      setManualEdit(true);
    }
  }, [value, manualEdit]);

  if (warning) {
    return (
      <FormSection
        helpText={paragraph_24_zusammenfassung_helpText}
        label="Zugeordnete Einheiten"
      >
        <Alert
          color={AlertPresets.warning.color}
          title="Achtung - Die zusammengefassten Anlagen gemäß § 24 EEG können noch nicht geladen werden:"
        >
          {warning}
        </Alert>
      </FormSection>
    );
  }

  if (!defaultGenerators) return null;

  const data: ComboboxData = defaultGenerators.map((generator) => ({
    value: String(generator.id),
    label: lockedValues.includes(generator.id || 0)
      ? `${generator.name} (${generator.site_name}) 🔒`
      : `${generator.name} (${generator.site_name})`,
    disabled: lockedValues.includes(generator.id || 0)
  }));

  const handleManualEditToggle = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!event.currentTarget.checked) {
      onChange(null);
    } else {
      onChange(defaultGenerators.map((gen) => gen.id));
    }
    setManualEdit(event.currentTarget.checked);
  };

  return (
    <FormSection
      helpText={paragraph_24_zusammenfassung_helpText}
      isCollapsible
      label="Zugeordnete Einheiten"
    >
      <CustomFormFieldWrapper
        error={error}
        {...GENERATOR_FORM_DATA[
          "einheit.eeg_anlage.paragraph_24_zusammenfassung.custom_generator_ids"
        ]}
      >
        <Group>
          <MultiSelect
            aria-label={
              GENERATOR_FORM_DATA[
                "einheit.eeg_anlage.paragraph_24_zusammenfassung.custom_generator_ids"
              ].label
            }
            data={data}
            disabled={!manualEdit}
            flex={1}
            value={
              value?.map(String) ||
              defaultGenerators.map((gen) => String(gen.id))
            }
            onChange={(newValues) => onChange(newValues.map(Number))}
          />
          <Switch
            checked={manualEdit}
            label="Manuelle Zuordnung"
            onChange={handleManualEditToggle}
          />
        </Group>
      </CustomFormFieldWrapper>
      <Paragraph24ZusammenfassungTable
        data={defaultGenerators
          .filter((gen) => (value ? value.includes(gen.id) : true))
          .map((gen) => ({
            label: gen.name,
            installedCapacity: String(gen.installed_capacity),
            site: String(gen.site_name),
            eeg_inbetriebnahmedatum: String(gen.eeg_inbetriebnahmedatum)
          }))}
      />
    </FormSection>
  );
}

export default Paragraph24Zusammenfassung;
